import { AlertCircle, Package } from "lucide-react"
import { useInfiniteHits } from "react-instantsearch"
import { Product } from "../../types/search"
import { cn } from "../../utils/cn"
import { ProductCard } from "./ProductCard"

interface SearchResultsProps {
	onProductClick?: (product: Product) => void
	className?: string
}

/**
 * SearchResults displays the product grid with infinite scroll
 * Uses InstantSearch's useInfiniteHits hook for pagination
 */
export const SearchResults = ({ onProductClick, className }: SearchResultsProps) => {
	const { hits, isLastPage, showMore, sendEvent } = useInfiniteHits<Product & { objectID: string }>()

	// Handle product click with analytics
	const handleProductClick = (hit: any, product: Product) => {
		// Send click event to InstantSearch for analytics
		sendEvent("click", hit, "Product Clicked")

		// Call parent handler
		if (onProductClick) {
			onProductClick(product)
		}
	}

	// Empty state
	if (hits.length === 0) {
		return (
			<div className={cn("flex flex-col items-center justify-center py-16 px-4 text-center", className)}>
				<Package className="w-16 h-16 text-muted-foreground/30 mb-4" />
				<h3 className="text-lg font-medium mb-2">No products found</h3>
				<p className="text-sm text-muted-foreground max-w-md">
					Try adjusting your search terms or filters to find what you're looking for.
				</p>
			</div>
		)
	}

	return (
		<div className={cn("flex flex-col gap-6", className)}>
			{/* Product Grid */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
				{hits.map((hit) => {
					const product: Product = {
						id: hit.objectID,
						name: hit.name,
						description: hit.description,
						category: hit.category,
						price: hit.price,
						originalPrice: hit.originalPrice,
						currency: hit.currency,
						image: hit.image,
						images: hit.images,
						rating: hit.rating,
						reviewCount: hit.reviewCount,
						inStock: hit.inStock,
						stockQuantity: hit.stockQuantity,
						brand: hit.brand,
						url: hit.url,
						tags: hit.tags,
						metadata: hit.metadata,
						badge: hit.badge,
					}
					return <ProductCard key={hit.objectID} onClick={() => handleProductClick(hit, product)} product={product} />
				})}
			</div>

			{/* Load More Button */}
			{!isLastPage && (
				<div className="flex justify-center py-4">
					<button
						className="flex items-center gap-2 px-6 py-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
						onClick={showMore}>
						<span>Load More Products</span>
					</button>
				</div>
			)}
		</div>
	)
}

/**
 * SearchResultsSkeleton shows loading state while search is in progress
 */
export const SearchResultsSkeleton = ({ className }: { className?: string }) => {
	return (
		<div className={cn("flex flex-col gap-6", className)}>
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
				{[...Array(8)].map((_, i) => (
					<div className="flex flex-col rounded-lg border border-border bg-card overflow-hidden" key={i}>
						{/* Image skeleton */}
						<div className="aspect-square bg-muted animate-pulse" />

						{/* Content skeleton */}
						<div className="flex flex-col gap-2 p-4">
							<div className="h-3 bg-muted rounded animate-pulse w-1/2" />
							<div className="h-4 bg-muted rounded animate-pulse w-full" />
							<div className="h-4 bg-muted rounded animate-pulse w-3/4" />
							<div className="h-3 bg-muted rounded animate-pulse w-1/3 mt-2" />
							<div className="h-6 bg-muted rounded animate-pulse w-1/2 mt-auto" />
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

/**
 * SearchResultsError shows error state when search fails
 */
export const SearchResultsError = ({ error, onRetry }: { error?: string; onRetry?: () => void }) => {
	return (
		<div className="flex flex-col items-center justify-center py-16 px-4 text-center">
			<AlertCircle className="w-16 h-16 text-destructive mb-4" />
			<h3 className="text-lg font-medium mb-2">Search Error</h3>
			<p className="text-sm text-muted-foreground max-w-md mb-4">
				{error || "Something went wrong while searching. Please try again."}
			</p>
			{onRetry && (
				<button
					className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
					onClick={onRetry}>
					Try Again
				</button>
			)}
		</div>
	)
}
